import axios from 'axios';
import { API_URL } from './config';

const api = axios.create({
  baseURL: API_URL,
  timeout: 25000, // 25s : assez pour les exports lourds, empêche les freeze infinis
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('kadolo_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('kadolo_token');
      localStorage.removeItem('kadolo_user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth
export const login = (credentials) => api.post('/auth/login', credentials);
export const getMe = () => api.get('/auth/me');

// Users
export const getUsers = () => api.get('/users');
export const getUser = (id) => api.get(`/users/${id}`);
export const createUser = (data) => api.post('/users', data);
export const updateUser = (id, data) => api.put(`/users/${id}`, data);
export const deleteUser = (id) => api.delete(`/users/${id}`);
export const downloadUserFichePDF = (userId, resetPassword = false) => 
  api.get(`/users/${userId}/fiche-pdf`, { params: { reset_password: resetPassword }, responseType: 'blob' });
export const sendUserFicheEmail = (userId, resetPassword = false) => 
  api.post(`/users/${userId}/fiche-email`, null, { params: { reset_password: resetPassword } });

// Sites
export const getSites = () => api.get('/sites');
export const getSite = (id) => api.get(`/sites/${id}`);
export const createSite = (data) => api.post('/sites', data);
export const updateSite = (id, data) => api.put(`/sites/${id}`, data);
export const deleteSite = (id) => api.delete(`/sites/${id}`);

// Sociétés de livraison
export const getSocietesLivraison = (params) => api.get('/societes-livraison', { params });
export const getSocieteLivraison = (id) => api.get(`/societes-livraison/${id}`);
export const createSocieteLivraison = (data) => api.post('/societes-livraison', data);
export const updateSocieteLivraison = (id, data) => api.put(`/societes-livraison/${id}`, data);
export const deleteSocieteLivraison = (id) => api.delete(`/societes-livraison/${id}`);
export const exportLivraisonsExcel = (params) => api.get('/societes-livraison/export/livraisons', { params, responseType: 'blob' });

// Dossiers
export const getDossiers = (params) => api.get('/dossiers', { params });
export const getDossier = (id) => api.get(`/dossiers/${id}`);
export const searchDossierByCredentials = (telephone, id_ticket) => api.get('/dossiers/search/by-credentials', { params: { telephone, id_ticket } });
export const createDossier = (data) => api.post('/dossiers', data);
export const updateDossier = (id, data) => api.put(`/dossiers/${id}`, data);
export const deleteDossier = (id) => api.delete(`/dossiers/${id}`);

// Verification & Validation
export const verifyGagnant = (dossierId, data) => api.post(`/dossiers/${dossierId}/verify`, data);
export const validateRemise = (dossierId, data) => api.post(`/dossiers/${dossierId}/validate`, data);
export const refuseRemise = (dossierId, raison) => api.post(`/dossiers/${dossierId}/refuse`, null, { params: { raison } });

// Remise par procuration
export const enregistrerProcuration = (dossierId, data) => api.post(`/dossiers/${dossierId}/remise-procuration`, data);
export const confirmerRemiseProcurationMobile = (dossierId, data) => api.post(`/dossiers/${dossierId}/remise-procuration/confirmer-mobile`, data);
export const validerProcurationBO = (dossierId) => api.post(`/dossiers/${dossierId}/remise-procuration/valider-bo`);
export const renvoyerSmsProcuration = (dossierId, type) => api.post(`/dossiers/${dossierId}/remise-procuration/renvoyer-sms`, { type });
export const annulerProcuration = (dossierId) => api.delete(`/dossiers/${dossierId}/remise-procuration`);

// OTP
export const sendOTP = (dossierId, phoneNumber = null) => api.post(`/dossiers/${dossierId}/otp/send`, phoneNumber ? { phone_number: phoneNumber } : {});
export const verifyOTP = (dossierId, code) => api.post(`/dossiers/${dossierId}/otp/verify`, { code });
export const getOTPStatus = (dossierId) => api.get(`/dossiers/${dossierId}/otp/status`);
export const getOTPCodeAdmin = (dossierId) => api.get(`/dossiers/${dossierId}/otp/active`);

// Preuves
export const getPreuves = (dossierId) => api.get(`/preuves/${dossierId}`);
export const createPreuve = (dossierId, data) => api.post(`/preuves/base64`, {
  dossier_id: dossierId,
  type_preuve: data.type_preuve,
  fichier_base64: data.fichier_base64 || data.base64_data,
  commentaire: data.description || data.commentaire
});
export const deletePreuve = (id) => api.delete(`/preuves/${id}`);

// Anomalies
export const getAnomalies = (params) => api.get('/anomalies', { params });
export const getAnomalie = (id) => api.get(`/anomalies/${id}`);
export const createAnomalie = (data) => api.post('/anomalies', data);
export const updateAnomalie = (id, data) => api.put(`/anomalies/${id}`, data);
export const deleteAnomalie = (id) => api.delete(`/anomalies/${id}`);

// Audit
export const getAuditLogs = (params) => api.get('/audit', { params });

// Stats
export const getDashboardStats = () => api.get('/stats/dashboard');
export const getStatsBySite = () => api.get('/stats/par-site');
export const getGraphStats = (params) => api.get('/stats/graphs', { params });

// Settings (Admin only)
export const getSettings = () => api.get('/settings');
export const updateSettings = (data) => api.put('/settings', data);
export const previewOTPMessage = () => api.get('/settings/otp-preview');

// Permissions (Admin only)
export const getAllPermissions = () => api.get('/permissions');
export const getRolePermissions = (role) => api.get(`/permissions/${role}`);
export const updateRolePermissions = (role, data) => api.put(`/permissions/${role}`, data);

// Push Notifications
export const getVapidPublicKey = () => api.get('/push/vapid-public-key');
export const subscribePush = (subscription) => api.post('/push/subscribe', subscription);
export const unsubscribePush = (endpoint) => api.delete('/push/unsubscribe', { params: { endpoint } });
export const testPushNotification = () => api.post('/push/test');
export const notifyAllRemettants = () => api.post('/push/notify-remettants');

// Search
export const searchDossiers = (query) => api.get('/search/dossiers', { params: { q: query } });

// Export
export const exportDossiers = (params) => api.get('/export/dossiers', { params, responseType: 'blob' });
export const exportDossiersPDF = (params) => api.get('/export/dossiers/pdf', { params, responseType: 'blob' });
export const exportDossiersPDFEnAttente = () => api.get('/export/dossiers/pdf/en-attente', { responseType: 'blob' });
export const exportDossiersPDFASuivre = () => api.get('/export/dossiers/pdf/a-suivre', { responseType: 'blob' });
export const downloadDossierPDF = (dossierId) => api.get(`/dossiers/${dossierId}/pdf`, { responseType: 'blob' });
export const downloadPreuvesTransporteurPDF = (dossierId) => api.get(`/dossiers/${dossierId}/preuves-transporteur/pdf`, { responseType: 'blob' });
export const downloadImportTemplate = () => api.get('/templates/import-dossiers', { responseType: 'blob' });
export const importDossiers = (data) => api.post('/dossiers/import', data);

// Phone validation
export const validatePhone = (telephone) => api.post('/validate-phone', { telephone });

// Coupons retrait
export const getCouponRetrait = (id) => api.get(`/coupons/retrait/${id}`);
export const getCouponByDossier = (dossierId) => api.get(`/coupons/retrait/dossier/${dossierId}`);
export const generateCouponRetrait = (data) => api.post('/coupons/retrait', data);

// Fiche produit (historique)
export const getProduitHistorique = (produitId, lieuId) =>
  api.get(`/stocks/produit/${produitId}/historique`, { params: lieuId ? { lieu_id: lieuId } : {} });
export const downloadProduitHistoriquePDF = (produitId, lieuId) =>
  api.get(`/stocks/produit/${produitId}/historique/pdf`, {
    params: lieuId ? { lieu_id: lieuId } : {},
    responseType: 'blob',
  });
export const downloadCouponPDF = (couponId) => api.get(`/coupons/retrait/${couponId}/pdf`, { responseType: 'blob' });
export const downloadTicketBeneficiairePDF = (couponId) => api.get(`/coupons/retrait/${couponId}/ticket-beneficiaire-pdf`, { responseType: 'blob' });
export const resendCouponEmail = (couponId) => api.post(`/coupons/retrait/${couponId}/resend-email`);
export const resendCouponSMS = (couponId) => api.post(`/coupons/retrait/${couponId}/resend-sms`);

// Fournisseurs
export const getFournisseurs = () => api.get('/fournisseurs');
export const getFournisseur = (id) => api.get(`/fournisseurs/${id}`);

// Produits
export const getProduits = () => api.get('/produits');
export const getProduit = (id) => api.get(`/produits/${id}`);

// My (Mobile)
export const getMyDossiers = () => api.get('/my/dossiers');
export const getMyRemises = () => api.get('/my/remises');
export const getMyAnomalies = () => api.get('/my/anomalies');
export const getMyExpires = () => api.get('/my/expires');

// Seed
export const seedDatabase = () => api.post('/seed');

export default api;
